/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * NextEventCard / SuggestedEvents
 * -------------------------------
 * The rail's event cards. "Next up" is the first of athlete_upcoming_events —
 * the caller's registered events that are not over yet, soonest first; its
 * button opens /app/my-registrations, where the QR pass lives. "Events for you" lists
 * published events that have not started, skipping ones already registered.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import FeedIcon from './FeedIcon';
import { EVENTS_PATH, REGISTRATIONS_PATH } from '../../pages/feed/feedRoutes';
import { eventStart } from '../../pages/feed/feedApi';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const eventPath = (event) => `${EVENTS_PATH}/${encodeURIComponent(event.id)}`;

/** Whole days from now until the event starts (0 = today). */
export function daysUntil(event, now = new Date()) {
  const start = eventStart(event);
  if (!Number.isFinite(start)) return null;
  const a = new Date(now); a.setHours(0, 0, 0, 0);
  const b = new Date(start); b.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((b - a) / 86400000));
}

function when(event) {
  const s = eventStart(event);
  if (!Number.isFinite(s)) return '';
  const d = new Date(s);
  let h = d.getHours();
  const ap = h < 12 ? 'AM' : 'PM';
  h = h % 12 || 12;
  return `${DOW[d.getDay()]}, ${d.getDate()} ${MON[d.getMonth()]} · ${h}:${String(d.getMinutes()).padStart(2, '0')} ${ap}`;
}

export function NextEventCard({ events, isLoading }) {
  if (isLoading) return <div className="fd-skel" style={{ height: 210, borderRadius: 22 }} aria-hidden="true" />;
  const event = events?.[0];
  if (!event) {
    return (
      <section className="fd-rc">
        {/* <h3>No upcoming events</h3> */}
        {/* <p className="fd-empty-rc">Pick a race or ride to train for — it shows up here with a countdown.</p> */}
        <h3>No events registered yet</h3>
        <p className="fd-empty-rc">Register for a race or ride and it&rsquo;ll show up here with a countdown.</p>
        <Link to={EVENTS_PATH} className="fd-btn fd-btn-primary" style={{ marginTop: 14, width: '100%' }}>
          <FeedIcon name="calendar_month" />Browse events
        </Link>
      </section>
    );
  }
  const days = daysUntil(event);
  const others = events.length - 1;
  return (
    <section className="fd-rc fd-nx" aria-label="Your next event">
      <span className="fd-eyebrow">Next up · Registered</span>
      {/* Title two lines, time one, location two — each ends in an ellipsis,
          and the tooltip carries the full text. */}
      <h3 className="fd-nx-title" title={event.title}><Link to={eventPath(event)}>{event.title}</Link></h3>
      {when(event) && (
        <p title={when(event)}><FeedIcon name="calendar_month" /><span className="fd-nx-time">{when(event)}</span></p>
      )}
      {event.location && (
        <p title={event.location}><FeedIcon name="location_on" /><span className="fd-nx-loc">{event.location}</span></p>
      )}
      <div className="row2">
        {days !== null && (
          <div><b>{days}</b><span>{days === 0 ? 'today' : days === 1 ? 'day to go' : 'days to go'}</span></div>
        )}
        <Link to={REGISTRATIONS_PATH} className="fd-btn fd-btn-primary"><FeedIcon name="qr_code_2" />View pass</Link>
      </div>
      {others > 0 && (
        <Link to={REGISTRATIONS_PATH} className="fd-more-reg">+ {others} more registered {others === 1 ? 'event' : 'events'}</Link>
      )}
    </section>
  );
}

export function SuggestedEvents({ events, registeredIds, isLoading }) {
  const list = (events || []).filter((e) => !registeredIds.has(e.id)).slice(0, 3);
  return (
    <section className="fd-rc" aria-label="Events for you">
      <div className="fd-rc-h"><h3>Events for you</h3><Link to={EVENTS_PATH} className="fd-link">See all</Link></div>
      {isLoading && <div className="fd-skel" style={{ height: 150, marginTop: 10 }} aria-hidden="true" />}
      {!isLoading && !list.length && <p className="fd-empty-rc">No events open for registration right now.</p>}
      <div className="fd-evl">
        {list.map((e) => {
          const s = eventStart(e);
          const d = Number.isFinite(s) ? new Date(s) : null;
          return (
            <div className="fd-evi" key={e.id}>
              <span className="dt">{d && <><span>{MON[d.getMonth()]}</span><b>{d.getDate()}</b></>}</span>
              <div style={{ minWidth: 0 }}>
                <Link to={eventPath(e)}><b className="t">{e.title}</b></Link>
                {e.location && <span className="m">{e.location}</span>}
              </div>
              <Link to={eventPath(e)} className="fd-btn-sm">View</Link>
            </div>
          );
        })}
      </div>
    </section>
  );
}
